import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom hook for speech-to-text using Web Speech API
 * Provides voice recording and real-time transcription functionality
 */
function useSpeechRecognition(lang) {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(false);

  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const shouldBeRecordingRef = useRef(false);
  const internalTranscriptRef = useRef('');

  // Recording timeout in milliseconds (2 minutes)
  const RECORDING_TIMEOUT = 120000;

  // Check browser compatibility on mount
  useEffect(() => {
    const supported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
    setIsSupported(supported);

    if (supported) {
      // Initialize recognition instance
      const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
      recognitionRef.current = new SpeechRecognition();

      // Configure recognition
      recognitionRef.current.lang = lang || 'es-ES'; // Default Spanish if not provided
      recognitionRef.current.continuous = true; // Continuous recording
      recognitionRef.current.interimResults = true; // Real-time results
      recognitionRef.current.maxAlternatives = 1; // Only best match
    }
  }, []);

  // Update language if prop changes after initialization
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = lang || 'es-ES';
    }
  }, [lang]);

  // Cleanup function
  const cleanup = useCallback(() => {
    shouldBeRecordingRef.current = false;

    if (recognitionRef.current) {
      try {
        // Remove event handlers to prevent memory leaks
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      } catch (err) {
        console.error('Error stopping recognition:', err);
      }
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Transfer internal transcript to exposed transcript when stopping
    if (internalTranscriptRef.current) {
      setTranscript(internalTranscriptRef.current.trim());
    }

    setIsRecording(false);
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError('Speech recognition not supported in this browser');
      return;
    }

    if (!recognitionRef.current) {
      setError('Speech recognition not initialized');
      return;
    }

    // Prevent multiple starts
    if (shouldBeRecordingRef.current) {
      return;
    }

    try {
      setError(null);
      shouldBeRecordingRef.current = true;
      internalTranscriptRef.current = '';

      // Set up event handlers
      recognitionRef.current.onresult = (event) => {
        // Reconstruct complete transcript from all results (final + interim)
        let completeTranscript = '';

        for (let i = 0; i < event.results.length; i++) {
          const transcriptPart = event.results[i][0].transcript;
          completeTranscript += transcriptPart + ' ';
        }

        // Update internal transcript with the complete text
        internalTranscriptRef.current = completeTranscript.trim();
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);

        if (event.error === 'not-allowed') {
          setError('Microphone access denied');
        } else if (event.error === 'no-speech') {
          setError('No speech detected');
        } else if (event.error === 'network') {
          setError('Network error - speech recognition requires internet connection');
        } else {
          setError(`Recognition error: ${event.error}`);
        }

        cleanup();
      };

      recognitionRef.current.onend = () => {
        // Only restart if we should still be recording
        if (shouldBeRecordingRef.current) {
          try {
            recognitionRef.current.start();
          } catch (err) {
            console.error('Error restarting recognition:', err);
            cleanup();
          }
        } else {
          setIsRecording(false);
        }
      };

      // Start recognition
      recognitionRef.current.start();
      setIsRecording(true);

      // Set timeout to auto-stop after 2 minutes
      timeoutRef.current = setTimeout(() => {
        stopRecording();
        setError('Recording stopped automatically (2 minute limit)');
      }, RECORDING_TIMEOUT);
    } catch (err) {
      console.error('Error starting recognition:', err);
      setError('Failed to start recording');
      cleanup();
    }
  }, [isSupported, cleanup]);

  // Stop recording
  const stopRecording = useCallback(() => {
    cleanup();
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isRecording,
    transcript,
    error,
    isSupported,
    startRecording,
    stopRecording,
  };
}

export default useSpeechRecognition;
